import {Component} from './base';
import {Moment} from 'moment';
import * as moment from 'moment';

interface EventDateRecord {
  id: number;
  title: string;
  start: string;
  end: string;
  description: string | null;
}

export class PanelCalendarComponent extends Component {
  private ajaxUrl: string;
  private eventName: string;
  private dates: EventDateRecord[] = [];
  private selectedDate: Moment | null = null;
  private selectedEventId: number | null = null;
  private isSubmitting = false;

  public static shouldLoad() {
    return $('.panel-calendar').length > 0;
  }

  public load() {
    this.ajaxUrl = $('#hidden-ajaxurl').val() as string;
    this.eventName = $('#hidden-eventname').val() as string;

    this.bindComposer();
    this.initCalendar();
    this.resetComposer();
    this.renderQuickPicks();
    this.renderTimezone();
    this.loadUpcomingDates();
  }

  public reload() {
    $('.panel-calendar').fullCalendar('refetchEvents');
    this.loadUpcomingDates();
  }

  private bindComposer() {
    $('#date-composer-date, #date-composer-time, #date-composer-duration')
      .on('change input', () => {
        this.selectedEventId = null;
        this.syncSelectedDateFromInput();
        this.highlightSelectedEventCard();
        this.updatePreview();
        this.updateDurationPresetState();
      });

    $('#date-composer-description').on('input', () => {
      this.selectedEventId = null;
      this.highlightSelectedEventCard();
      this.updatePreview();
    });

    $('#date-composer-submit').on('click', () => {
      this.submitComposer();
    });

    $('#date-composer-reset').on('click', () => {
      this.resetComposer();
      this.showFeedback('info', 'Composer reset. Pick a slot and add the next date when you are ready.');
    });

    $('#date-composer-today').on('click', () => {
      this.selectDate(moment());
    });

    $('body').on('click', '.js-date-quick-pick', (event) => {
      const element = $(event.currentTarget);
      const date = element.data('date') as string;
      this.selectDate(moment(date, 'YYYY-MM-DD'));
    });

    $('body').on('click', '.js-date-duration-preset', (event) => {
      const element = $(event.currentTarget);
      $('#date-composer-duration').val(element.data('duration'));
      this.updateDurationPresetState();
      this.updatePreview();
    });

    $('body').on('click', '.js-date-template', (event) => {
      const element = $(event.currentTarget);
      const dateId = parseInt(element.data('dateId') as string, 10);
      this.useDateAsTemplate(dateId);
    });

    $('body').on('click', '.js-date-remove', (event) => {
      const element = $(event.currentTarget);
      const dateId = parseInt(element.data('dateId') as string, 10);
      this.removeDate(dateId);
    });
  }

  private initCalendar() {
    $('.panel-calendar').fullCalendar({
      timezone: 'local',
      firstDay: 1,
      height: 'auto',
      header: {
        left: 'prev,next today',
        center: 'title',
        right: ''
      },
      eventLimit: false,
      timeFormat: 'H:mm',
      displayEventEnd: true,
      events: (start: Moment, end: Moment, timezone: string, callback: (events: EventDateRecord[]) => void) => {
        this.fetchDates(start, end)
          .then((events) => {
            callback(events);
            this.highlightCalendarSelection();
          })
          .catch(() => {
            callback([]);
          });
      },
      viewRender: () => {
        this.highlightCalendarSelection();
      },
      dayClick: (date: Moment) => {
        this.selectDate(date);
      },
      eventClick: (calendarEvent: any) => {
        this.useDateAsTemplate(parseInt(calendarEvent.id, 10));
        return false;
      },
      eventRender: (calendarEvent: any, element: JQuery) => {
        const start = moment(calendarEvent.start);
        const end = moment(calendarEvent.end);
        element.attr('title', `${start.format('LLLL')} - ${end.format('LT')}`);
      }
    });
  }

  private async fetchDates(start: Moment, end: Moment): Promise<EventDateRecord[]> {
    return await $.ajax({
      url: this.ajaxUrl,
      dataType: 'json',
      data: {
        start: start.clone().startOf('day').format(),
        end: end.clone().endOf('day').format(),
      }
    });
  }

  private async loadUpcomingDates() {
    try {
      const dates = await this.fetchDates(moment().startOf('day'), moment().add(12, 'months').endOf('month'));
      this.dates = dates.sort((left, right) => {
        return moment(left.start).valueOf() - moment(right.start).valueOf();
      });

      this.renderUpcomingDates();
      this.renderStats();
    } catch (error) {
      this.showFeedback('danger', 'Unable to load the upcoming dates right now.');
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }

  private renderTimezone() {
    const timeZone = typeof Intl !== 'undefined' && Intl.DateTimeFormat
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : 'your browser';

    $('#date-browser-timezone').text(timeZone || 'your browser');
  }

  private renderQuickPicks() {
    const container = $('#date-quick-picks');
    const today = moment().startOf('day');
    const baseDate = this.selectedDate && this.selectedDate.isAfter(today.clone().add(6, 'days'))
      ? this.selectedDate.clone()
      : today;
    const selectedValue = this.selectedDate ? this.selectedDate.format('YYYY-MM-DD') : '';

    let html = '';
    for (let offset = 0; offset < 7; offset++) {
      const day = baseDate.clone().add(offset, 'days');
      const isSelected = day.format('YYYY-MM-DD') === selectedValue;
      html += `
        <button type="button" class="mc-date-quick-pick ${isSelected ? 'mc-date-quick-pick--active' : ''} js-date-quick-pick" data-date="${day.format('YYYY-MM-DD')}">
          <span class="mc-date-quick-pick__day">${day.format('ddd')} </span>
          <span class="mc-date-quick-pick__date">${day.format('DD MMM')}</span>
        </button>
      `;
    }

    container.html(html);
  }

  private selectDate(date: Moment) {
    const normalizedDate = date.clone().local().startOf('day');

    this.selectedDate = normalizedDate;
    this.selectedEventId = null;
    $('#date-composer-date').val(normalizedDate.format('YYYY-MM-DD'));
    $('.panel-calendar').fullCalendar('gotoDate', normalizedDate);
    this.renderQuickPicks();
    this.highlightCalendarSelection();
    this.highlightSelectedEventCard();
    this.updatePreview();
  }

  private syncSelectedDateFromInput() {
    const dateValue = $('#date-composer-date').val() as string;
    if (!dateValue) {
      return;
    }

    this.selectedDate = moment(dateValue, 'YYYY-MM-DD');
    $('.panel-calendar').fullCalendar('gotoDate', this.selectedDate);
    this.renderQuickPicks();
    this.highlightCalendarSelection();
  }

  private highlightCalendarSelection() {
    const selectedValue = this.selectedDate ? this.selectedDate.format('YYYY-MM-DD') : null;
    const dayCells = $('.panel-calendar .fc-day');

    dayCells.removeClass('mc-date-calendar__day--selected');

    if (!selectedValue) {
      return;
    }

    dayCells.filter(`[data-date="${selectedValue}"]`).addClass('mc-date-calendar__day--selected');
  }

  private updateDurationPresetState() {
    const duration = parseInt($('#date-composer-duration').val() as string, 10);
    $('.js-date-duration-preset').each((index, element) => {
      const button = $(element);
      const isActive = parseInt(button.data('duration') as string, 10) === duration;
      button.toggleClass('mc-date-chip--active', isActive);
    });
  }

  private buildComposerStart(): Moment | null {
    const dateValue = $('#date-composer-date').val() as string;
    const timeValue = $('#date-composer-time').val() as string;

    if (!dateValue || !timeValue) {
      return null;
    }

    const start = moment(`${dateValue} ${timeValue}`, 'YYYY-MM-DD HH:mm');
    return start.isValid() ? start : null;
  }

  private getComposerDuration(): number {
    return parseInt($('#date-composer-duration').val() as string, 10);
  }

  private updatePreview() {
    const previewMain = $('#date-preview-main');
    const previewMeta = $('#date-preview-meta');
    const start = this.buildComposerStart();
    const duration = this.getComposerDuration();

    if (!start || !duration || duration <= 0) {
      previewMain.text('Select a date, time, and duration to preview the slot.');
      previewMeta.text('You can still click the month view to jump the composer to another day.');
      return;
    }

    const end = start.clone().add(duration, 'minutes');
    previewMain.text(`${start.format('dddd, D MMMM YYYY')} · ${start.format('LT')} - ${end.format('LT')}`);

    previewMeta.text(`One date will be added for ${this.eventName}.`);
  }

  private resetComposer() {
    this.selectedEventId = null;
    this.selectedDate = moment().startOf('day');
    $('#date-composer-date').val(this.selectedDate.format('YYYY-MM-DD'));
    $('#date-composer-time').val('19:00');
    $('#date-composer-duration').val('60');
    $('#date-composer-description').val('');
    $('.panel-calendar').fullCalendar('gotoDate', this.selectedDate);
    this.renderQuickPicks();
    this.highlightCalendarSelection();
    this.highlightSelectedEventCard();
    this.updateDurationPresetState();
    this.updatePreview();
  }

  private async submitComposer() {
    if (this.isSubmitting) {
      return;
    }

    const start = this.buildComposerStart();
    const duration = this.getComposerDuration();
    const descriptionValue = ($('#date-composer-description').val() as string || '').trim();
    const description = descriptionValue.length > 0 ? descriptionValue : null;

    if (!start) {
      this.showFeedback('danger', 'Choose a date and start time first.');
      return;
    }

    if (start.clone().startOf('day').isBefore(moment().startOf('day'))) {
      this.showFeedback('danger', 'Choose a day today or in the future.');
      return;
    }

    if (!duration || duration <= 0) {
      this.showFeedback('danger', 'Duration needs to be greater than zero.');
      return;
    }

    this.isSubmitting = true;
    $('#date-composer-submit').prop('disabled', true);

    try {
      const occurrenceEnd = start.clone().add(duration, 'minutes');
      const result = await this.saveEventDate(start, occurrenceEnd, description);

      if (result) {
        this.showFeedback('success', `Added a new date for ${this.eventName}.`);
        this.reload();
      }
    } finally {
      this.isSubmitting = false;
      $('#date-composer-submit').prop('disabled', false);
    }
  }

  private async saveEventDate(start: Moment, until: Moment, description: string | null) {
    try {
      return await $.ajax({
        type: 'POST',
        url: this.ajaxUrl,
        data: JSON.stringify({
          start: start.toISOString(),
          until: until.toISOString(),
          description: description
        }),
        contentType: 'application/json',
        dataType: 'json'
      });
    } catch (error) {
      this.showAjaxError('Error while creating the date.', error);
    }

    return false;
  }

  private async removeDate(id: number) {
    const record = this.findDateById(id);
    const label = record ? moment(record.start).format('dddd, D MMMM YYYY [at] LT') : 'this date';

    if (!window.confirm(`Remove ${label}?`)) {
      return;
    }

    try {
      await $.ajax({
        type: 'DELETE',
        url: `${this.ajaxUrl}/${id}`,
      });

      this.showFeedback('success', `Removed ${label}.`);
      this.reload();
    } catch (error) {
      this.showAjaxError('Error while deleting the date.', error);
    }
  }

  private renderStats() {
    $('#date-stat-total').text(this.dates.length.toString());

    if (this.dates.length === 0) {
      $('#date-stat-next').text('None yet');
      return;
    }

    $('#date-stat-next').text(moment(this.dates[0].start).format('D MMM · LT'));
  }

  private renderUpcomingDates() {
    const container = $('#date-existing-list');

    if (this.dates.length === 0) {
      container.html(`
        <div class="mc-date-empty">
          <i class="fa fa-calendar-o mc-date-empty__icon"></i>
          <p class="mc-date-empty__title">No upcoming dates yet</p>
          <p class="mc-date-empty__copy">Use the composer on the left or click a day in the calendar to create the first slot.</p>
        </div>
      `);
      return;
    }

    container.html(this.dates.map((record) => this.renderDateCard(record)).join(''));
    this.highlightSelectedEventCard();
  }

  private renderDateCard(record: EventDateRecord): string {
    const start = moment(record.start);
    const end = moment(record.end);
    const duration = this.formatDuration(end.diff(start, 'minutes'));
    const description = record.description ? this.escapeHtml(record.description) : 'No description';
    const isActive = record.id === this.selectedEventId;

    return `
      <article class="mc-date-card ${isActive ? 'mc-date-card--active' : ''}" data-date-id="${record.id}">
        <div class="mc-date-card__header">
          <div>
            <p class="mc-date-card__title">${start.format('dddd, D MMMM YYYY')}</p>
            <p class="mc-date-card__meta">${start.format('LT')} - ${end.format('LT')} · ${duration}</p>
          </div>
          <span class="mc-date-card__badge">${this.escapeHtml(this.eventName)}</span>
        </div>
        <p class="mc-date-card__description">${description}</p>
        <div class="mc-date-card__actions">
          <button type="button" class="mc-date-secondary-button js-date-template" data-date-id="${record.id}">
            Use as template
          </button>
          <button type="button" class="mc-date-danger-button js-date-remove" data-date-id="${record.id}">
            Remove
          </button>
        </div>
      </article>
    `;
  }

  private highlightSelectedEventCard() {
    $('.mc-date-card').removeClass('mc-date-card--active');

    if (this.selectedEventId !== null) {
      $(`.mc-date-card[data-date-id="${this.selectedEventId}"]`).addClass('mc-date-card--active');
    }
  }

  private useDateAsTemplate(id: number) {
    const record = this.findDateById(id);
    if (!record) {
      return;
    }

    const start = moment(record.start);
    const end = moment(record.end);

    this.selectDate(start);
    this.selectedEventId = id;
    $('#date-composer-time').val(start.format('HH:mm'));
    $('#date-composer-duration').val(end.diff(start, 'minutes').toString());
    $('#date-composer-description').val(record.description || '');
    this.highlightSelectedEventCard();
    this.updateDurationPresetState();
    this.updatePreview();
    this.showFeedback('info', 'Loaded an existing date back into the composer so you can reuse it.');
  }

  private findDateById(id: number): EventDateRecord | null {
    for (const record of this.dates) {
      if (record.id === id) {
        return record;
      }
    }

    return null;
  }

  private formatDuration(totalMinutes: number): string {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (hours > 0) {
      return `${hours}h`;
    }
    return `${minutes}m`;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private showFeedback(type: 'success' | 'info' | 'danger', message: string) {
    $('#date-feedback')
      .removeClass('mc-date-feedback--hidden mc-date-feedback--success mc-date-feedback--info mc-date-feedback--danger')
      .addClass(`mc-date-feedback--${type}`)
      .text(message);
  }

  private showAjaxError(prefix: string, error: any) {
    let message = prefix;

    if (error && error.responseJSON && error.responseJSON.errors) {
      const errorKeys = Object.keys(error.responseJSON.errors);
      if (errorKeys.length > 0) {
        message = `${prefix} ${error.responseJSON.errors[errorKeys[0]]}`;
      }
    }

    this.showFeedback('danger', message);
    // eslint-disable-next-line no-console
    console.error(error);
  }
}



// WEBPACK FOOTER //
// ./~/source-map-loader!./app/Resources/assets/js/components/panel-calendar.ts